import { api } from "@/comn";
import { ptlEnvs } from "@/tra/tancis/ptli/comn";
import { TFormSchema, TGridSchema, useFetch } from "@/comn/hooks";
import { comnEnvs, comnUtils } from "@/comn/utils";

/*
 * Survey Question Management Service
 * !== 설문 질문 관리 서비스 ==!
 */

/*
 * @ BASE
 * @ 서비스에서 사용하는 기본 정보 세팅
 * @ path : 화면들의 기본 웹 경로
 * @ api : 사용하는 api 의 기본 주소
 * @ api 주소가 여러개일 경우 _ 로구분하여 의미에 맞게 카멜표기업으로 표시 : api_clr
 * @ nodes : 화면의 Page.Navigation 에서 사용하는 기본 결로 Array
 */

export const BASE = {
    path: `${ptlEnvs.base}/surv/ques`,
    api: `${process.env.REACT_APP_API_PTLI}/api/v1/ptli/surv/ques`,
    nodes: [],
};

/*
 * @ URLS
 * @ 화면에서 사용하는 주소들 정의
 * @ 해당화면의 컴포넌트명 대문자로 정의
 */
export const URLS = {
    ptli0419001q: `${BASE.path}/ptli0419001q`,
    ptli0419002s: `${BASE.path}/ptli0419002s`,
    ptli0419003s: `${BASE.path}/ptli0419003s`,
    ptli0419004q: `${BASE.path}/ptli0419004q`,
};

/*
 * @ APIS
 * @ 화면에서 사용하는 api 정의
 */
export const APIS = {
    getSurvQuesList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },

    createSurvQues: (data: any) => {
        return api.post(`${BASE.api}`, {
            ...data,
        });
    },

    updateSurvQuesInfo: (data: any) => {
        return api.put(`${BASE.api}/${data.quesId}`, { ...data });
    },
    getSurvQuesInfo: (qestnrId: any, quesId: any) => {
        return api.get(`${BASE.api}/${qestnrId}/${quesId}`);
    },

    deleteSurvQuesInfo: (data: any) => {
        return api.delete(`${BASE.api}/${data.quesId}`, { data });
    },

    getNextQuesOrd: (qestnrId: string) => {
        return api.get(`${BASE.api}/quesOrd`, {
            params: { qestnrId },
        });
    },
    getNextItmOrd: (quesId: string) => {
        return api.get(`${BASE.api}/itmOrd`, {
            params: { quesId },
        });
    },

    deleteSurvItmInfo: (data: any) => {
        return api.delete(`${process.env.REACT_APP_API_PTLI}/api/v1/ptli/surv/itm/${data.quesItmId}`, { data });
    },
};

export const SF_SURV_QUES_LIST_SRCH: TFormSchema = {
    id: "form_SurvQuesListSrch",
    schema: {
        bsopClsfCd: {
            type: "select",
            label: "L_BSOP_CLSF_CD",
            all: true,
            area: "comnCd",
            comnCd: "COM0024",
        },
        ptlTpCd: {
            type: "select",
            label: "L_PTL_TP_CD",
            area: "comnCd",
            comnCd: "COM0052",
            excludes: ["B"],
            editType: "both",
        },
        qustnrTtle: { type: "text", label: "L_QUSTNR_TTLE" },
        rgsrUsr: { type: "text", label: "L_RGSR_USR" },
        quesCn: { type: "text", label: "L_QUES_CN" },
        qestnrId: { type: "text", label: "L_QESTNR_ID" },
    },
};

export const SF_SURV_QUES_INFO: TFormSchema = {
    id: "form_survQuesInfo",
    schema: {
        quesTpCd: {
            type: "radio",
            label: "L_QUES_TP_CD",
            area: "comnCd",
            comnCd: "PTL0061",
            defaultValue: "1",
        },
        quesCnKO: { type: "text", label: "L_QUES_CN_KO", required: true }, 
        quesCnEN: { type: "text", label: "L_QUES_CN_EN", required: true }, 
        quesCnTZ: { type: "text", label: "L_QUES_CN_TZ", required: true }, //질문 내용
        maxSeltCnt: {
            // 최대 선택 건수
            type: "radio",
            label: "L_MAX_SELT_CNT",
            options: [
                { label: "1", value: "1" },
                { label: "2", value: "2" },
                { label: "3", value: "3" },
                { label: "4", value: "4" },
                { label: "5", value: "5" },
            ],
        },
        rgsrUsr: { type: "text", label: "L_RGSR_USR" },
        qestnrId: { type: "text", label: "L_QUSTNR_ID" }, // 설문지 정보
        qustnrTtle: { type: "text", label: "L_QUSTNR_TTLE" },
        quesOrd: { type: "text", label: "L_QUES_ORD" }, // 질문 순번
    },
};

export const SF_SURV_ITM_INFO: TFormSchema = {
  id: "form_SurvItmInfo",
  schema: {
    bsopClsfCd: { type: "select", label: "L_BSOP_CLSF_CD", all: true, area: "comnCd", comnCd: "COM0024" },
    ptlTpCd:    { type: "select", label: "L_PTL_TP_CD", area: "comnCd", comnCd: "COM0052", excludes: ["B"], required: true },
    qustnrTtle: { type: "text", label: "L_QUSTNR_TTLE" },
    quesCn:     { type: "text", label: "L_QUES_CN" },
    qestnrId:   { type: "text", label: "L_QUSTNR_ID" },
    quesId:     { type: "text", label: "L_QUES_ID" },

    // 아이템 배열
    items: {
      type: "array",
      schema: {
        itmOrd:   { type: "text", label: "L_ITM_ORD"  },    
        itmCnEN:  { type: "text", label: "L_ITM_CN_EN", required: true },
        itmCnTZ:  { type: "text", label: "L_ITM_CN_TZ", required: true },
        itmCnKO:  { type: "text", label: "L_ITM_CN_KO", required: true },
        quesItmId:{ type: "text", label: "L_QUES_ITM_ID" },       // 수정/삭제 시 사용
      },
    },
  },
};

export const SG_SURV_QUES_LIST: TGridSchema = {
    id: "grid_SurvQuesList",
    options: { pagination: "out", index: true },
    head: [
        { cells: [{ header: "L_BSOP_CLSF_NM", binding: "bsopClsfCdNm", width: "*" }] ,id: "bsopClsfCd" },
        { cells: [{ header: "L_PTL_TP_CD", binding: "ptlTpCdNm", width: "*" }] ,id: "ptlTpCd" },
        { cells: [{ header: "L_QUSTNR_TTLE", binding: "qustnrTtle", width: "*" }] },
        { cells: [{ header: "L_QUES_CN", binding: "quesCn", width: "*" }] },
        { cells: [{ header: "L_QUES_TP_CD", binding: "quesTpCdNm", width: "*" }], id: "quesTpCd"},
        { cells: [{ header: "L_FRST_REGST_ID", binding: "frstRegstId", width: "*" }] },
        { cells: [{ header: "L_FRST_RGSR_DTM", binding: "frstRgsrDtm", width: "15%" }] },
    ],
    body: [
        { cells: [{ binding: "bsopClsfCdNm", align: "center" }] },
        { cells: [{ binding: "ptlTpCdNm", align: "center" }] },
        { cells: [{ binding: "qustnrTtle", align: "left" }] },
        { cells: [{ binding: "quesCn", align: "left" }] },
        { cells: [{ binding: "quesTpCdNm", align: "center" }] },
        { cells: [{ binding: "frstRegstId", align: "center" }] },
        { cells: [{ binding: "frstRgsrDtm", type: "datetime" }] },
    ],
};

export const SG_SURV_QUES_LANG_LIST : TGridSchema = {
    id: "grid_SurvQuesLangList",
    head: [
        { cells: [{ binding: "langCd"}] },
        { cells: [{ binding: "quesCn"}] },
    ],
    body: [
        { cells: [{ binding: "langCd"}] },
        { cells: [{ binding: "quesCn"}] },
    ],
};

export const SG_SURV_ITM_LANG_LIST : TGridSchema = {
id: "grid_SurvItmLangList",
head: [
    { cells: [{ binding: "itmOrd"}] },
    { cells: [{ binding: "langCd"}] },
    { cells: [{ binding: "itmCn"}] },
],
body: [
    { cells: [{ binding: "itmOrd"}] },
    { cells: [{ binding: "langCd"}] },
    { cells: [{ binding: "itmCn"}] },
],
};